/**
 * i18n Configuration for Multi-Language Support
 *
 * EPIC-16: Supports 8 languages:
 * - English (en) - Default
 * - Traditional Chinese (zh-TW)
 * - Simplified Chinese (zh-CN)
 * - Japanese (ja)
 * - Korean (ko)
 * - Spanish (es)
 * - French (fr)
 * - Portuguese (pt)
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import translation files
import en from './locales/en.json';
import zhTW from './locales/zh-TW.json';
import zhCN from './locales/zh-CN.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import pt from './locales/pt.json';

/**
 * Supported language codes
 */
export const SUPPORTED_LANGUAGES = [
  'en',
  'zh-TW',
  'zh-CN',
  'ja',
  'ko',
  'es',
  'fr',
  'pt',
] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

/**
 * Language display names (native names for each language)
 */
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  'en': 'English',
  'zh-TW': '繁體中文',
  'zh-CN': '简体中文',
  'ja': '日本語',
  'ko': '한국어',
  'es': 'Español',
  'fr': 'Français',
  'pt': 'Português',
};

/**
 * Initialize i18next with translation resources
 */
i18n
  .use(initReactI18next) // Pass i18n instance to react-i18next
  .init({
    // Translation resources
    resources: {
      en: { translation: en },
      'zh-TW': { translation: zhTW },
      'zh-CN': { translation: zhCN },
      ja: { translation: ja },
      ko: { translation: ko },
      es: { translation: es },
      fr: { translation: fr },
      pt: { translation: pt },
    },

    // Default language
    lng: 'en',

    // Fallback language if translation is missing
    fallbackLng: 'en',

    // Disable in-context editing
    debug: false,

    // Namespace configuration
    ns: ['translation'],
    defaultNS: 'translation',

    // Key separator (use dot notation for nested keys)
    keySeparator: '.',

    // Interpolation configuration
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // React-specific options
    react: {
      useSuspense: false, // Don't use Suspense for loading translations
    },
  });

export default i18n;
